import {
	Capacitor,
	type PluginListenerHandle,
	registerPlugin,
} from '@capacitor/core';
import { extractSmsCode } from '@supply-mover-2-0/core';

/**
 * PD-17379 — the Android storefront app's SmsRetriever plugin, which lives in
 * the app itself (apps/ecommerce-mobile/android/.../SmsRetrieverPlugin.java)
 * rather than in an npm package. Google's SMS Retriever hands the app the
 * sign-in text when the text carries the app's hash, so the code fills in
 * without the customer typing it.
 */
interface SmsRetrieverPluginModel {
	start(): Promise<void>;
	stop(): Promise<void>;
	addListener(
		eventName: 'smsReceived',
		listener: (event: { message?: string }) => void,
	): Promise<PluginListenerHandle>;
	addListener(
		eventName: 'smsTimeout',
		listener: () => void,
	): Promise<PluginListenerHandle>;
}

const PLUGIN_NAME = 'SmsRetriever';

const SmsRetriever = registerPlugin<SmsRetrieverPluginModel>(PLUGIN_NAME);

export interface SmsCodeListenerModel {
	/**
	 * The app's applicationId, sent with the code request so the login server
	 * puts this app's hash in the text.
	 */
	androidAppId: string;
	/** Stop listening. Safe to call more than once. */
	stop: () => void;
}

/**
 * True only inside an Android app build that has the plugin. A live web
 * update can run this code in an app installed before the plugin existed;
 * there the plugin is missing and nothing happens, the customer types the
 * code as before.
 */
export function canListenForSmsCode(): boolean {
	return (
		Capacitor.getPlatform() === 'android' &&
		Capacitor.isPluginAvailable(PLUGIN_NAME)
	);
}

/**
 * Start listening for the code text. Call it BEFORE asking for the code, and
 * send the returned `androidAppId` with the request: only a listening app may
 * ask for a text with its hash, since that text is shorter (it leaves out the
 * sign-in link the app cannot use anyway).
 *
 * `onCode` gets the six-digit code once, and listening stops by itself after
 * a text or after Google's five-minute timeout. Null when this is not an app
 * that can listen, or listening could not start; ask for the code without an
 * app id then.
 */
export async function listenForSmsCode(
	onCode: (code: string) => void,
): Promise<SmsCodeListenerModel | null> {
	if (!canListenForSmsCode()) {
		return null;
	}

	const handles: Array<PluginListenerHandle> = [];
	let stopped = false;
	const stop = (): void => {
		if (stopped) {
			return;
		}
		stopped = true;
		for (const handle of handles) {
			void handle.remove();
		}
		SmsRetriever.stop().catch(() => {
			// Already stopped natively.
		});
	};

	try {
		const { App } = await import('@capacitor/app');
		const { id: androidAppId } = await App.getInfo();

		handles.push(
			await SmsRetriever.addListener('smsReceived', (event) => {
				if (stopped) {
					return;
				}
				stop();
				const code = extractSmsCode(event.message);
				if (code != null) {
					onCode(code);
				}
			}),
		);
		handles.push(await SmsRetriever.addListener('smsTimeout', stop));
		await SmsRetriever.start();

		return { androidAppId, stop };
	} catch (error) {
		console.debug('[smsRetriever] could not listen for the code text', error);
		stop();
		return null;
	}
}
